/* ==========================================================================
   ABIs — narrowed to exactly what the page calls.

   Trimmed on purpose: a frontend that carries a contract's full ABI invites
   calling something the UI was never designed for, and every entry here is a
   surface this page actually uses.
   ========================================================================== */

/** THE OPEN FAUCET. Artifact: contracts/src/faucet/OpenFaucet.sol */
export const openFaucetAbi = [
  // --- pricing: the only authority, read for both display and maxCbBTC ---
  {
    type: 'function',
    name: 'quote',
    stateMutability: 'view',
    inputs: [{ name: 'quantity', type: 'uint256' }],
    outputs: [{ name: 'totalSats', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'currentPrice',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'seedsFor',
    stateMutability: 'pure',
    inputs: [
      { name: 'quantity', type: 'uint256' },
      { name: 'enhanced', type: 'bool' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'leavesFor',
    stateMutability: 'pure',
    inputs: [
      { name: 'quantity', type: 'uint256' },
      { name: 'enhanced', type: 'bool' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  // --- state ---
  { type: 'function', name: 'totalDraws', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'opened', stateMutability: 'view', inputs: [], outputs: [{ type: 'bool' }] },
  { type: 'function', name: 'paused', stateMutability: 'view', inputs: [], outputs: [{ type: 'bool' }] },
  { type: 'function', name: 'canary', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { type: 'function', name: 'treasury', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  {
    type: 'function',
    name: 'canDraw',
    stateMutability: 'view',
    inputs: [{ name: 'who', type: 'address' }],
    outputs: [{ type: 'bool' }],
  },
  { type: 'function', name: 'MAX_DRAWS_PER_TX', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  {
    type: 'function',
    name: 'LEAVES_PER_ENHANCED_DRAW',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  // --- the only write ---
  {
    type: 'function',
    name: 'draw',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'quantity', type: 'uint256' },
      { name: 'enhanced', type: 'bool' },
      { name: 'maxCbBTC', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    type: 'event',
    name: 'FaucetDrawn',
    inputs: [
      { name: 'participant', type: 'address', indexed: true },
      { name: 'enhanced', type: 'bool', indexed: true },
      { name: 'quantity', type: 'uint256', indexed: false },
      { name: 'leavesRetired', type: 'uint256', indexed: false },
      { name: 'cbBTCPaid', type: 'uint256', indexed: false },
      { name: 'seedsMinted', type: 'uint256', indexed: false },
      { name: 'firstDrawNumber', type: 'uint256', indexed: false },
      { name: 'totalDrawsAfter', type: 'uint256', indexed: false },
    ],
  },
  // --- errors, so a revert renders as a sentence rather than a hex blob ---
  { type: 'error', name: 'CanaryOnly', inputs: [] },
  { type: 'error', name: 'DrawsPaused', inputs: [] },
  {
    type: 'error',
    name: 'PriceExceedsMax',
    inputs: [
      { name: 'required', type: 'uint256' },
      { name: 'maxAccepted', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'InvalidQuantity',
    inputs: [
      { name: 'quantity', type: 'uint256' },
      { name: 'max', type: 'uint256' },
    ],
  },
] as const

/** Taboshi Seeds. Read-only from this page — minting belongs to the Faucet alone. */
export const taboshiSeedsAbi = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'id', type: 'uint256' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'totalMinted',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  { type: 'function', name: 'faucet', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
] as const

/** cbBTC, through the plain ERC-20 surface. */
export const erc20Abi = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
] as const

/**
 * Legacy taboshi1, through the ERC-1155 surface an enhanced draw needs.
 *
 * NOTE there is no `burnBatch` here. The participant never burns directly: they
 * grant `setApprovalForAll` and the Faucet performs the retirement inside the
 * draw, atomically. Exposing a burn entry point on this page would let a user
 * destroy leaves without receiving SEED.
 */
export const erc1155Abi = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'id', type: 'uint256' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'isApprovedForAll',
    stateMutability: 'view',
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'operator', type: 'address' },
    ],
    outputs: [{ type: 'bool' }],
  },
  {
    type: 'function',
    name: 'setApprovalForAll',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'approved', type: 'bool' },
    ],
    outputs: [],
  },
] as const

/**
 * SIP ROUTER — dormant future route. A sip is immediate: pay ~$25 of cbBTC, receive
 * SEED from Router inventory in the same transaction. Narrowed to exactly what the
 * TAKE A SIP option calls; the Router's internals are not surfaced.
 */
export const sipRouterAbi = [
  {
    type: 'function',
    name: 'previewSip',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'sipSats', type: 'uint256' },
      { name: 'seedOut', type: 'uint256' },
    ],
  },
  {
    type: 'function',
    name: 'paused',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'bool' }],
  },
  {
    type: 'function',
    name: 'inventory',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'sip',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'maxCbBTC', type: 'uint256' },
      { name: 'minSeedOut', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    type: 'event',
    name: 'SipTaken',
    inputs: [
      { name: 'participant', type: 'address', indexed: true },
      { name: 'cbBTCPaid', type: 'uint256', indexed: false },
      { name: 'seedReceived', type: 'uint256', indexed: false },
    ],
  },
  // Errors, so a revert renders as a sentence rather than a hex blob.
  { type: 'error', name: 'SipsPaused', inputs: [] },
  {
    type: 'error',
    name: 'PriceExceedsMax',
    inputs: [
      { name: 'required', type: 'uint256' },
      { name: 'maxAccepted', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'SeedBelowMinimum',
    inputs: [
      { name: 'seedOut', type: 'uint256' },
      { name: 'minRequired', type: 'uint256' },
    ],
  },
  {
    type: 'error',
    name: 'InsufficientInventory',
    inputs: [
      { name: 'needed', type: 'uint256' },
      { name: 'held', type: 'uint256' },
    ],
  },
  { type: 'error', name: 'ZeroSeedOut', inputs: [] },
  { type: 'error', name: 'InvalidOracleAnswer', inputs: [] },
  {
    type: 'error',
    name: 'StaleOracle',
    inputs: [
      { name: 'updatedAt', type: 'uint256' },
      { name: 'maxAge', type: 'uint256' },
    ],
  },
] as const
